import { forwardRef } from "react";

export const cn = (...c) => c.filter(Boolean).join(" ");

export const Button = forwardRef(function Button(
  { variant = "primary", size = "md", className = "", children, ...rest }, ref
) {
  const base =
    "inline-flex items-center justify-center gap-2 font-semibold rounded-md transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-brand-500/30 disabled:opacity-50 disabled:cursor-not-allowed";
  const sizes = {
    sm: "px-3 py-1.5 text-sm",
    md: "px-4 py-2.5 text-sm",
    lg: "px-5 py-3.5 text-base",
  };
  const variants = {
    primary: "bg-brand-500 text-white hover:bg-brand-600 shadow-sm",
    secondary: "bg-white text-brand-500 border border-brand-500 hover:bg-brand-50",
    ghost: "text-ink-700 hover:bg-tertiary",
    danger: "bg-danger text-white hover:brightness-110",
    outline: "border border-brand-500 text-brand-500 hover:bg-brand-50",
  };
  return (
    <button ref={ref} className={cn(base, sizes[size], variants[variant], className)} {...rest}>
      {children}
    </button>
  );
});

export function Input({ className = "", label, error, id, ...rest }) {
  return (
    <label className="block text-sm">
      {label && <span className="block text-[12px] font-semibold uppercase tracking-[1px] text-ink-900 mb-1.5">{label}</span>}
      <input
        id={id}
        className={cn(
          "w-full rounded-md bg-white border border-[#D6D2CC] px-3.5 py-3 text-ink-900 placeholder:text-ink-300",
          "focus:outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-500/15",
          error && "border-danger",
          className,
        )}
        {...rest}
      />
      {error && <span className="text-xs text-danger mt-1 block">{error}</span>}
    </label>
  );
}

export function Select({ className = "", label, children, ...rest }) {
  return (
    <label className="block text-sm">
      {label && <span className="block text-[12px] font-semibold uppercase tracking-[1px] text-ink-900 mb-1.5">{label}</span>}
      <select
        className={cn(
          "w-full rounded-md bg-white border border-[#D6D2CC] px-3.5 py-3 text-ink-900",
          "focus:outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-500/15",
          className,
        )}
        {...rest}
      >
        {children}
      </select>
    </label>
  );
}

export function Card({ className = "", children, ...rest }) {
  return (
    <div className={cn("bg-surface border border-tertiary rounded-lg shadow-sm", className)} {...rest}>
      {children}
    </div>
  );
}

export function Badge({ children, tone = "neutral" }) {
  // tones return solid colored pills (per layout spec)
  const tones = {
    neutral: "bg-[#6B7280] text-white",
    bruto: "bg-[#6B7280] text-white",
    em_beneficiamento: "bg-warn text-white",
    beneficiada: "bg-ok text-white",
    success: "bg-ok text-white",
    warn: "bg-warn text-white",
    danger: "bg-danger text-white",
    info: "bg-info text-white",
  };
  return (
    <span className={cn("inline-flex items-center rounded-md px-2 py-1 text-[10px] font-bold uppercase tracking-[0.6px]", tones[tone] || tones.neutral)}>
      {children}
    </span>
  );
}

export function Empty({ title = "Sem dados", hint }) {
  return (
    <div className="text-center py-16 px-4">
      <div className="text-ink-300 font-mono text-xs mb-2 tracking-wide">/* nada por aqui */</div>
      <h3 className="font-heading text-lg font-semibold text-ink-900">{title}</h3>
      {hint && <p className="text-sm text-ink-500 mt-1">{hint}</p>}
    </div>
  );
}

export function Spinner({ size = 20 }) {
  return (
    <svg className="animate-spin text-brand-500" width={size} height={size} viewBox="0 0 24 24" fill="none">
      <circle className="opacity-20" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-90" fill="currentColor" d="M4 12a8 8 0 0 1 8-8v4a4 4 0 0 0-4 4H4z" />
    </svg>
  );
}

export function fmtBRL(v) {
  const n = Number(v || 0);
  return n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}
export function fmtNum(v, dec = 2) {
  const n = Number(v || 0);
  return n.toLocaleString("pt-BR", { minimumFractionDigits: dec, maximumFractionDigits: dec });
}
export function fmtKg(v) { return `${fmtNum(v, 2)} kg`; }
